import { useCallback, useLayoutEffect, useRef, useState } from "react";

type TUseFillViewportHeightOptions = {
  /** Space reserved below the element for surrounding UI (pagination row, sticky footer, page padding). */
  bottomGap?: number;
  /** Never shrink the scroll area below this, so it stays usable on short viewports. */
  minHeight?: number;
};

/**
 * Compute a max-height that makes an element fill the remaining viewport space from its
 * current top down to the bottom, so its inner content scrolls instead of overflowing the page.
 *
 * The value is derived from the element's live position (recomputed on resize / layout changes),
 * so no fixed height is hard-coded.
 */
export const useFillViewportHeight = <T extends HTMLElement = HTMLDivElement>({
  bottomGap = 0,
  minHeight = 240,
}: TUseFillViewportHeightOptions = {}) => {
  const ref = useRef<T>(null);
  const [maxHeight, setMaxHeight] = useState<number>();

  const recalc = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    const available = window.innerHeight - top - bottomGap;
    setMaxHeight(Math.max(available, minHeight));
  }, [bottomGap, minHeight]);

  useLayoutEffect(() => {
    recalc();
    window.addEventListener("resize", recalc);
    const observer = new ResizeObserver(recalc);
    observer.observe(document.body);
    return () => {
      window.removeEventListener("resize", recalc);
      observer.disconnect();
    };
  }, [recalc]);

  return { ref, maxHeight };
};
